import React, { useRef } from "react"
import { useNavigate } from "@tanstack/react-router"
import { Button, Dropdown, Typography } from "@mimber/ui-web"
import {
  MoreOutlined,
  EditOutlined,
  SendOutlined,
  StopOutlined,
  DeleteOutlined,
  CopyOutlined,
} from "@mimber/ui-web"
import { usePermissionedMenuItems } from "@/core/hooks/usePermissionedMenuItems"
import {
  PublishEventModal,
  DeleteEventModal,
  CancelEventModal,
} from "@/components/modals"
import { Resource, Action, EventStatus } from "@mimber/schema"
import { useDuplicateEvent } from "@mimber/client/mimber-organization"
import type { PermissionedMenuItem } from "@/core/hooks/usePermissionedMenuItems"
import type { EventActionsDropdownProps } from "./EventActionsDropdown.types"

const { Text } = Typography

export const EventActionsDropdown: React.FunctionComponent<
  EventActionsDropdownProps
> = ({
  event,
  onActionComplete,
  size = "small",
  type = "text",
  iconOnly = true,
}) => {
  const navigate = useNavigate()

  const isDraft = event.status === EventStatus.DRAFT
  const isPublished = event.status === EventStatus.PUBLISHED

  const publishOpenRef = useRef<(() => void) | null>(null)
  const cancelOpenRef = useRef<(() => void) | null>(null)
  const deleteOpenRef = useRef<(() => void) | null>(null)

  const duplicateEvent = useDuplicateEvent(event.uuid, {
    onSuccess: (response) => {
      navigate({
        to: "/events/$eventUuid/edit",
        params: { eventUuid: response.data.uuid },
      })
    },
  })

  const menuItems: PermissionedMenuItem[] = [
    {
      key: "edit",
      label: "Edit",
      icon: <EditOutlined />,
      resource: Resource.EVENT,
      action: Action.UPDATE,
      disabled: !isDraft && !isPublished,
      onClick: () =>
        navigate({
          to: "/events/$eventUuid/edit",
          params: { eventUuid: event.uuid },
        }),
    },
    ...(isDraft
      ? ([
          {
            key: "publish",
            label: "Publish",
            icon: <SendOutlined />,
            resource: Resource.EVENT,
            action: Action.UPDATE,
            onClick: () => publishOpenRef.current?.(),
          },
        ] as PermissionedMenuItem[])
      : []),
    ...(isPublished
      ? ([
          {
            key: "cancel",
            label: "Cancel Event",
            icon: <StopOutlined />,
            resource: Resource.EVENT,
            action: Action.UPDATE,
            danger: true,
            onClick: () => cancelOpenRef.current?.(),
          },
        ] as PermissionedMenuItem[])
      : []),
    {
      key: "duplicate",
      label: "Duplicate",
      icon: <CopyOutlined />,
      resource: Resource.EVENT,
      action: Action.CREATE,
      onClick: () => duplicateEvent.mutate(),
    },
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteOutlined />,
      danger: true,
      resource: Resource.EVENT,
      action: Action.DELETE,
      onClick: () => deleteOpenRef.current?.(),
    },
  ]

  const { items: permissionedItems } = usePermissionedMenuItems({
    items: menuItems,
  })

  if (permissionedItems.length === 0) {
    return <Text type="secondary">—</Text>
  }

  return (
    <>
      <PublishEventModal
        event={event}
        onSuccess={onActionComplete}
        renderButton={(open) => {
          publishOpenRef.current = open
          return <></>
        }}
      />
      <CancelEventModal
        event={event}
        onSuccess={onActionComplete}
        renderButton={(open) => {
          cancelOpenRef.current = open
          return <></>
        }}
      />
      <DeleteEventModal
        event={event}
        onSuccess={onActionComplete}
        renderButton={(open) => {
          deleteOpenRef.current = open
          return <></>
        }}
      />
      <Dropdown menu={{ items: permissionedItems }} trigger={["click"]}>
        <Button size={size} type={type} icon={<MoreOutlined />}>
          {!iconOnly && "Actions"}
        </Button>
      </Dropdown>
    </>
  )
}
